import React from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation } from "react-router-dom";

// A render error in any page used to unmount the whole React tree and leave a blank screen (and, during
// prerendering, a blank snapshot). This keeps the failure visible and recoverable instead:
//  - the error is still logged, so genuine bugs are never swallowed;
//  - the fallback page is noindex, so a crashed render can never be indexed as the page's content;
//  - prerender validation (scripts/prerender.js) fails on a noindex / generic-title page, so a crash
//    during the build is reported rather than written to disk.
class Boundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error("Page render failed:", error, info && info.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Navigating to another route gives the new page a fresh chance to render.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: "60vh", padding: "80px 20px", textAlign: "center", fontFamily: "'Poppins', sans-serif" }}>
        <Helmet>
          <title>Something went wrong | 360 SynergyTech</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700 }}>Something went wrong</h1>
        <p style={{ maxWidth: 480, margin: "12px auto 30px" }}>
          This page could not be displayed. Please try again, or return to the homepage.
        </p>
        <Link to="/" style={{ color: "#0162ca" }}>
          Back to Homepage
        </Link>
      </div>
    );
  }
}

export default function ErrorBoundary({ children }) {
  const location = useLocation();
  return <Boundary resetKey={location.pathname}>{children}</Boundary>;
}
